<template>
  <div class="home-v2">
    <!-- 首页轮播 -->
    <section class="home-carousel" @mouseenter="stopCarousel" @mouseleave="startCarousel">
      <div
        class="carousel-track"
        :style="{ transform: `translateX(-${activeBannerIndex * 100}%)` }"
        @mousedown="handleCarouselMouseDown"
        @touchstart="handleCarouselTouchStart"
        @touchend="handleCarouselTouchEnd"
      >
        <div
          v-for="item in homeBanners"
          :key="item.id"
          class="carousel-slide"
          :class="{ clickable: !!item.link }"
          @click="handleBannerClick(item, $event)"
          @dragstart.prevent
        >
          <img v-if="item.image" :src="item.image" :alt="item.title" />
          <div class="carousel-fallback" v-else></div>
        </div>
      </div>
      <button v-if="homeBanners.length > 1" class="carousel-btn prev" type="button" @click="prevBanner" aria-label="上一张">
        <span>‹</span>
      </button>
      <button v-if="homeBanners.length > 1" class="carousel-btn next" type="button" @click="nextBanner" aria-label="下一张">
        <span>›</span>
      </button>
      <div v-if="homeBanners.length > 1" class="carousel-dots">
        <button
          v-for="(item, index) in homeBanners"
          :key="item.id"
          type="button"
          :class="{ active: activeBannerIndex === index }"
          :aria-label="`切换到第${index + 1}张`"
          @click="goBanner(index)"
        ></button>
      </div>
    </section>

    <!-- 首页统计 -->
    <section class="hero">
      <div ref="heroStats" class="hero-stats-wrap">
        <div v-if="primaryStats.length" class="hero-stats hero-stats-primary">
          <div class="stat-card" v-for="item in primaryStats" :key="item.id">
            <div class="stat-value">
              <span class="stat-num">{{ displayStatNumber(item) }}</span>
              <span v-if="displayStatUnit(item.unit)" class="stat-unit">{{ displayStatUnit(item.unit) }}</span>
            </div>
            <div class="stat-label">{{ item.labelKey ? $t(item.labelKey) : item.label }}</div>
          </div>
        </div>
        <div v-if="secondaryStats.length" class="hero-stats hero-stats-secondary">
          <div class="stat-card" v-for="item in secondaryStats" :key="item.id">
            <div class="stat-value">
              <span class="stat-num">{{ displayStatNumber(item) }}</span>
              <span v-if="displayStatUnit(item.unit)" class="stat-unit">{{ displayStatUnit(item.unit) }}</span>
            </div>
            <div class="stat-label">{{ item.labelKey ? $t(item.labelKey) : item.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 关于我们 -->
    <section class="section about-v2">
      <div class="container-v2">
        <div class="about-left">
          <div class="section-header">
            <span class="section-tag">{{ $t('home.aboutTag') }}</span>
            <h2>{{ $t('home.aboutTitle') }}</h2>
          </div>
          <div class="about-text">
            <p v-for="(paragraph, index) in displayAboutParagraphs" :key="index">{{ paragraph }}</p>
            <router-link to="/about" class="link-arrow">{{ $t('common.learnMore') }} →</router-link>
          </div>
        </div>
        <div class="about-img">
          <video
            v-if="aboutVideoUrl"
            :src="aboutVideoUrl"
            controls
            playsinline
            preload="metadata"
          ></video>
          <img v-else :src="aboutImage" alt="about" />
        </div>
      </div>
    </section>

    <!-- 服务板块 -->
    <section class="section services-v2">
      <div class="container-v2">
        <div class="section-header center">
          <span class="section-tag">{{ $t('home.servicesTag') }}</span>
          <h2>{{ $t('home.servicesTitle') }}</h2>
          <p>{{ displayServiceSubtitle }}</p>
        </div>
        <div class="service-grid">
          <div class="service-card" v-for="(item, idx) in services" :key="item.id || idx" :class="'card-' + idx">
            <div class="card-icon" :class="{ 'has-image': item.iconImage }">
              <img v-if="item.iconImage" :src="item.iconImage" :alt="item.nameKey ? $t(item.nameKey) : item.name" />
              <span v-else>{{ item.icon }}</span>
            </div>
            <h3>{{ item.nameKey ? $t(item.nameKey) : item.name }}</h3>
            <p>{{ item.descKey ? $t(item.descKey) : item.desc }}</p>
            <router-link :to="item.path">{{ $t('common.details') }} →</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- 工程案例 -->
    <section class="section cases-v2">
      <div class="container-v2">
        <div class="section-header">
          <span class="section-tag">{{ $t('home.casesTag') }}</span>
          <h2>{{ $t('home.casesTitle') }}</h2>
        </div>
        <div class="cases-grid">
          <div class="case-card-v2" v-for="item in caseList" :key="item.id">
            <div class="case-img-v2">
              <img :src="item.image" :alt="item.title" />
            </div>
            <div class="case-info-v2">
              <h4>{{ item.title }}</h4>
              <p>{{ item.summary }}</p>
              <router-link :to="item.path">{{ $t('common.viewDetail') }}</router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 新闻中心 -->
    <section class="section news-v2">
      <div class="container-v2">
        <div class="section-header center">
          <span class="section-tag">{{ $t('home.newsTag') }}</span>
          <h2>{{ $t('home.newsTitle') }}</h2>
        </div>
        <div class="news-grid">
          <div class="news-card-v2" v-for="news in newsList" :key="news.id">
            <div class="news-date-v2">
              <span class="day">{{ news.day }}</span>
              <span class="month">{{ news.month }}</span>
            </div>
            <h4>{{ news.title }}</h4>
            <router-link :to="news.path || '/news/detail/' + news.id">{{ $t('common.readMore') }} →</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="cta-v2">
      <div class="container-v2">
        <h2>{{ $t('home.contactBlockTitle') }}</h2>
        <p>{{ $t('home.contactBlockText') }}</p>
        <router-link to="/contact" class="btn-primary large">{{ $t('common.contact') }}</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { getCaseCategoryList, getHomeData } from '@/api/portal'
import { resolveFileUrl } from '@/utils/request'
import { formatDay, formatNewsMonth, plainText, summaryText } from '@/utils/format'
import defaultAboutImage from '@/assets/images/banner1.svg'
import defaultBannerImage from '@/assets/images/banner4.svg'
import defaultCaseImage from '@/assets/images/case1.svg'

export default {
  name: 'HomeV2',
  data() {
    return {
      heroTitle: '专注电力工程服务',
      heroHighlight: '一站式综合服务平台',
      heroIntro: '山东盛博莱电力工程有限公司成立于2020年，主要从事电力技术咨询、电力调试、电力试验、电力检修、电力维护、培训、电厂托管运营、售电交易、绿电交易等业务。',
      serviceSubtitle: '聚天下英才，创智慧未来；铸造智慧电力，开创清洁能源',
      aboutSummary: '打造电力行业一站式综合服务平台',
      aboutImage: defaultAboutImage,
      aboutVideoUrl: '',
      aboutFromHomeConfig: false,
      aboutParagraphs: [
        '公司业务覆盖电力技术咨询、电力调试、电力试验、电力检修、电力维护、培训、电厂托管运营、技术监督、性能试验、售电交易、绿电交易、电能优化等领域。'
      ],
      homeBanners: [
        {
          id: 1,
          title: '电力行业一站式综合服务平台',
          subtitle: '覆盖调试、试验、检修、维护、培训、电厂托管运营、售电交易与绿电交易',
          tag: 'SENBOLE POWER',
          image: defaultBannerImage,
          link: '/about/profile'
        },
        {
          id: 2,
          title: '铸造智慧电力，开创清洁能源',
          subtitle: '以专业技术与项目经验服务火电、新能源、垃圾发电、生物质发电、核电配套等多类电力项目',
          tag: 'POWER SERVICE',
          image: defaultBannerImage,
          link: '/cases/debug'
        }
      ],
      activeBannerIndex: 0,
      carouselTimer: null,
      carouselTouchX: 0,
      carouselMouseX: 0,
      carouselMouseDragging: false,
      carouselMouseMoved: false,
      statsAnimated: false,
      statsObserver: null,
      statsAnimationFrames: [],
      stats: [],
      services: [
        { icon: '◈', nameKey: 'home.serviceDebug', descKey: 'home.serviceDebugDesc', path: '/cases/debug' },
        { icon: '◉', nameKey: 'home.serviceTest', descKey: 'home.serviceTestDesc', path: '/cases/debug' },
        { icon: '◆', nameKey: 'home.serviceMaintain', descKey: 'home.serviceMaintainDesc', path: '/cases/operation' },
        { icon: '◇', nameKey: 'home.serviceTrade', descKey: 'home.serviceTradeDesc', path: '/contact' }
      ],
      caseList: [
        { id: 1, title: '印尼莫罗瓦利拉波塔电厂调试项目', summary: '5×380MW 发电调试', image: defaultCaseImage, path: '/cases/debug/1' },
        { id: 2, title: '青海格尔木光伏与熔盐储能调试项目', summary: '500MW光伏+50MW熔盐储能', image: defaultCaseImage, path: '/cases/debug/2' },
        { id: 3, title: '广东廉江核电厂用电源系统调试项目', summary: '1、2号机组厂用电源系统', image: defaultCaseImage, path: '/cases/debug/3' },
        { id: 4, title: '马来西亚Cypark垃圾焚烧发电项目', summary: '1×600t/d+1×19.73MW', image: defaultCaseImage, path: '/cases/debug/4' }
      ],
      newsList: [
        { id: 1, title: '中共山东盛博莱电力工程有限公司支部委员会成立', day: '15', month: '2023.04', path: '/news/detail/1' },
        { id: 2, title: '盛博莱电力持续完善电力行业一站式综合服务平台', day: '10', month: '2026.05', path: '/news/detail/2' },
        { id: 3, title: '公司员工培训工作持续推进专业能力建设', day: '10', month: '2026.05', path: '/news/detail/3' },
        { id: 4, title: '盛博莱电力公益活动走进山区助学慰问', day: '10', month: '2026.05', path: '/news/detail/4' }
      ]
    }
  },
  created() {
    this.loadHomeData()
  },
  mounted() {
    this.startCarousel()
    this.prepareStatsAnimation()
  },
  beforeDestroy() {
    this.stopCarousel()
    this.removeCarouselMouseListeners()
    this.destroyStatsAnimation()
  },
  computed: {
    displayHeroTitle() {
      return this.$langState.locale === 'en' ? this.$t('home.heroTitle') : this.heroTitle
    },
    displayHeroIntro() {
      return this.$langState.locale === 'en' ? this.$t('home.heroIntro') : this.heroIntro
    },
    displayAboutSummary() {
      return this.$langState.locale === 'en' ? this.$t('home.aboutSummary') : this.aboutSummary
    },
    displayAboutParagraphs() {
      if (this.$langState.locale === 'en') {
        return [
          'The company provides power technical consulting, commissioning, testing, overhaul, maintenance, training, plant operation outsourcing, technical supervision, performance testing, power trading and green power consulting services.'
        ]
      }
      return this.aboutParagraphs
    },
    displayServiceSubtitle() {
      return this.$langState.locale === 'en' ? this.$t('home.serviceSubtitle') : this.serviceSubtitle
    },
    primaryStats() {
      return this.stats.filter(item => this.getStatRowNum(item) === 1)
    },
    secondaryStats() {
      return this.stats.filter(item => this.getStatRowNum(item) !== 1)
    }
  },
  methods: {
    loadHomeData() {
      getHomeData().then(data => {
        this.applyHomeConfig(data.homeConfig)
        this.applyBanners(data.banners)
        this.applyProfile(data.profile)
        this.applyStats(data.stats)
        this.applyCases(data.cases)
        this.applyNews(data.news)
      }).catch(() => {
        this.applyStats([])
      })
      this.loadServiceCategories()
    },
    applyHomeConfig(config) {
      if (!config) {
        return
      }
      if (config.homeTitle) {
        const parts = config.homeTitle.split(/\n|，|,/).filter(Boolean)
        this.heroTitle = parts[0] || this.heroTitle
        this.heroHighlight = parts.slice(1).join(' ') || this.heroHighlight
      }
      if (config.profileText) {
        this.heroIntro = config.profileText
        this.aboutSummary = plainText(config.profileText)
        this.aboutParagraphs = this.formatAboutParagraphs(config.profileText, false)
        this.aboutFromHomeConfig = true
      }
      if (config.valuesText) {
        this.serviceSubtitle = config.valuesText
      }
      this.aboutVideoUrl = resolveFileUrl(config.aboutVideoUrl)
    },
    applyBanners(list) {
      if (!list || !list.length) {
        return
      }
      this.homeBanners = list.map(item => ({
        id: item.bannerId,
        title: item.bannerTitle || '',
        subtitle: item.subtitle || '',
        tag: item.tagText || '',
        image: resolveFileUrl(item.imageUrl, defaultBannerImage),
        link: item.linkUrl || ''
      }))
      this.activeBannerIndex = 0
      this.startCarousel()
    },
    applyProfile(profile) {
      if (!profile) {
        return
      }
      this.aboutImage = resolveFileUrl(profile.coverUrl, defaultAboutImage)
      if (this.aboutFromHomeConfig) {
        return
      }
      const text = plainText(profile.profileContent)
      if (text) {
        this.aboutParagraphs = this.formatAboutParagraphs(text)
      }
    },
    formatAboutParagraphs(text, shouldLimit = true) {
      const paragraphs = plainText(text).split(/\n+/).map(item => item.trim()).filter(Boolean)
      const visibleParagraphs = paragraphs.filter(paragraph => !this.isHiddenHomeAboutParagraph(paragraph))
      if (!shouldLimit) {
        return visibleParagraphs.length ? visibleParagraphs : [plainText(text)]
      }
      return visibleParagraphs.length > 1 ? visibleParagraphs.slice(0, 2) : [summaryText(visibleParagraphs[0] || text, 160)]
    },
    isHiddenHomeAboutParagraph(paragraph) {
      return /成立于2020年.*(全资子公司|现有员工566人)/.test(paragraph)
    },
    applyStats(list) {
      const stats = Array.isArray(list) ? list : []
      this.stats = stats.map((item, index) => {
        const valueMeta = this.extractStatUnit(item.statValue)
        return {
          id: item.statId || index,
          num: valueMeta.num,
          animatedNum: '',
          unit: valueMeta.unit,
          rowNum: this.normalizeStatRowNum(item.rowNum),
          label: item.statLabel || '',
          labelKey: ''
        }
      })
      this.statsAnimated = false
      this.$nextTick(() => {
        this.prepareStatsAnimation()
      })
    },
    extractStatUnit(value) {
      const text = String(value || '').trim()
      const unitMatch = text.match(/^(.+?)(人|项|年)$/)
      if (!unitMatch) {
        return { num: text, unit: '' }
      }
      return {
        num: unitMatch[1],
        unit: unitMatch[2]
      }
    },
    normalizeStatRowNum(rowNum) {
      const value = Number(rowNum)
      return value === 1 ? 1 : 2
    },
    getStatRowNum(item) {
      return this.normalizeStatRowNum(item && item.rowNum)
    },
    applyServices(list) {
      if (!list || !list.length) {
        return
      }
      const icons = ['◈', '◉', '◆', '◇']
      const nameKeys = ['home.serviceDebug', 'home.serviceTest', 'home.serviceMaintain', 'home.serviceTrade']
      const descKeys = ['home.serviceDebugDesc', 'home.serviceTestDesc', 'home.serviceMaintainDesc', 'home.serviceTradeDesc']
      this.services = list.slice(0, 4).map((item, index) => ({
        icon: icons[index] || '◈',
        iconImage: this.isImagePath(item.iconUrl) ? resolveFileUrl(item.iconUrl) : '',
        name: item.serviceTitle,
        desc: item.serviceDesc,
        nameKey: nameKeys[index] || '',
        descKey: descKeys[index] || '',
        path: item.linkUrl || '/cases/debug'
      }))
    },
    loadServiceCategories() {
      getCaseCategoryList().then(data => {
        this.applyCaseCategories(data)
      }).catch(() => {})
    },
    applyCaseCategories(list) {
      if (!list || !list.length) {
        return
      }
      const icons = ['◈', '◉', '◆', '◇', '□', '○']
      this.services = list.map((item, index) => ({
        id: item.categoryId,
        icon: icons[index % icons.length],
        iconImage: resolveFileUrl(item.categoryImage),
        name: item.categoryName || '',
        desc: item.categoryDesc || '',
        nameKey: '',
        descKey: '',
        path: {
          path: '/cases/operation',
          query: { categoryId: item.categoryId }
        }
      }))
    },
    applyCases(list) {
      if (!list || !list.length) {
        return
      }
      this.caseList = list.slice(0, 4).map(item => {
        const typePath = item.caseType === 'operation' ? 'operation' : 'debug'
        return {
          id: item.caseId,
          title: item.caseTitle,
          summary: item.summary || item.serviceScope || '',
          image: resolveFileUrl(item.coverUrl, defaultCaseImage),
          path: `/cases/${typePath}/${item.caseId}`
        }
      })
    },
    applyNews(list) {
      if (!list || !list.length) {
        return
      }
      this.newsList = list.slice(0, 4).map(item => ({
        id: item.newsId,
        title: item.newsTitle,
        day: formatDay(item.publishTime),
        month: formatNewsMonth(item.publishTime),
        path: `/news/detail/${item.newsId}`
      }))
    },
    isImagePath(url) {
      return !!url && (/^(https?:)?\/\//.test(url) || url.indexOf('/') === 0 || /\.(svg|png|jpe?g|gif|webp)$/i.test(url))
    },
    isExternalLink(url) {
      return /^(https?:)?\/\//.test(url)
    },
    startCarousel() {
      this.stopCarousel()
      if (this.carouselMouseDragging || !this.homeBanners || this.homeBanners.length <= 1) {
        return
      }
      this.carouselTimer = window.setInterval(() => {
        this.nextBanner()
      }, 5000)
    },
    stopCarousel() {
      if (this.carouselTimer) {
        window.clearInterval(this.carouselTimer)
        this.carouselTimer = null
      }
    },
    prevBanner() {
      const total = this.homeBanners.length
      this.activeBannerIndex = (this.activeBannerIndex - 1 + total) % total
      this.startCarousel()
    },
    nextBanner() {
      const total = this.homeBanners.length
      this.activeBannerIndex = (this.activeBannerIndex + 1) % total
      this.startCarousel()
    },
    goBanner(index) {
      this.activeBannerIndex = index
      this.startCarousel()
    },
    handleCarouselTouchStart(event) {
      this.carouselTouchX = event.changedTouches[0].clientX
    },
    handleCarouselTouchEnd(event) {
      const distance = event.changedTouches[0].clientX - this.carouselTouchX
      if (Math.abs(distance) < 50) {
        return
      }
      distance > 0 ? this.prevBanner() : this.nextBanner()
    },
    handleCarouselMouseDown(event) {
      if (event.button !== 0 || this.homeBanners.length <= 1) {
        return
      }
      event.preventDefault()
      this.carouselMouseX = event.clientX
      this.carouselMouseDragging = true
      this.carouselMouseMoved = false
      this.stopCarousel()
      window.addEventListener('mousemove', this.handleCarouselMouseMove)
      window.addEventListener('mouseup', this.handleCarouselMouseUp)
    },
    handleCarouselMouseMove(event) {
      if (!this.carouselMouseDragging) {
        return
      }
      if (Math.abs(event.clientX - this.carouselMouseX) > 8) {
        this.carouselMouseMoved = true
      }
    },
    handleCarouselMouseUp(event) {
      if (!this.carouselMouseDragging) {
        return
      }
      const distance = event.clientX - this.carouselMouseX
      this.carouselMouseDragging = false
      this.removeCarouselMouseListeners()
      if (Math.abs(distance) < 50) {
        this.startCarousel()
        return
      }
      distance > 0 ? this.prevBanner() : this.nextBanner()
    },
    removeCarouselMouseListeners() {
      window.removeEventListener('mousemove', this.handleCarouselMouseMove)
      window.removeEventListener('mouseup', this.handleCarouselMouseUp)
    },
    handleBannerClick(item, event) {
      if (this.carouselMouseMoved) {
        event.preventDefault()
        event.stopPropagation()
        this.carouselMouseMoved = false
        return
      }
      if (!item.link) {
        return
      }
      if (this.isExternalLink(item.link)) {
        window.open(item.link, '_blank', 'noopener')
        return
      }
      this.$router.push(item.link)
    },
    displayStatUnit(unit) {
      return unit
    },
    displayStatNumber(item) {
      return item.animatedNum !== undefined && item.animatedNum !== '' ? item.animatedNum : this.formatStatNumber(item.num, 0)
    },
    prepareStatsAnimation() {
      this.destroyStatsAnimation()
      this.stats.forEach(item => {
        this.$set(item, 'animatedNum', this.formatStatNumber(item.num, 0))
      })
      if (!this.$refs.heroStats) {
        return
      }
      if (!('IntersectionObserver' in window)) {
        this.startStatsAnimation()
        return
      }
      this.statsObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.startStatsAnimation()
        }
      }, { threshold: 0.35 })
      this.statsObserver.observe(this.$refs.heroStats)
    },
    startStatsAnimation() {
      if (this.statsAnimated) {
        return
      }
      this.statsAnimated = true
      if (this.statsObserver) {
        this.statsObserver.disconnect()
        this.statsObserver = null
      }
      const duration = 2600
      const startTime = performance.now()
      const animate = now => {
        const progress = Math.min((now - startTime) / duration, 1)
        const eased = 1 - Math.pow(1 - progress, 3)
        this.stats.forEach(item => {
          this.$set(item, 'animatedNum', this.formatStatNumber(item.num, eased))
        })
        if (progress < 1) {
          this.statsAnimationFrames.push(window.requestAnimationFrame(animate))
        }
      }
      this.statsAnimationFrames.push(window.requestAnimationFrame(animate))
    },
    destroyStatsAnimation() {
      this.statsAnimationFrames.forEach(id => window.cancelAnimationFrame(id))
      this.statsAnimationFrames = []
      if (this.statsObserver) {
        this.statsObserver.disconnect()
        this.statsObserver = null
      }
    },
    formatStatNumber(value, progress) {
      const text = String(value || '')
      const match = text.match(/-?[\d,]+(?:\.\d+)?/)
      if (!match) {
        return text
      }
      const rawNumber = match[0]
      const target = Number(rawNumber.replace(/,/g, ''))
      if (!Number.isFinite(target)) {
        return text
      }
      const decimals = rawNumber.includes('.') ? rawNumber.split('.')[1].length : 0
      const current = target * progress
      const numberText = decimals > 0 ? current.toFixed(decimals) : String(Math.floor(current))
      return text.slice(0, match.index) + numberText + text.slice(match.index + rawNumber.length)
    }
  }
}
</script>

<style lang="scss" scoped>
.container-v2 {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

.section {
  padding: 100px 0;
}

.home-carousel {
  position: relative;
  height: clamp(640px, 54vw, 880px);
  overflow: hidden;
  background: #0f172a;
}

.carousel-track {
  height: 100%;
  display: flex;
  transition: transform 0.65s ease;
  will-change: transform;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
}

.carousel-slide {
  position: relative;
  min-width: 100%;
  height: 100%;
  overflow: hidden;
  display: block;
  color: inherit;

  img,
  .carousel-fallback {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    user-select: none;
    pointer-events: none;
  }

  .carousel-fallback {
    background:
      linear-gradient(135deg, rgba(15, 23, 42, 0.2), rgba(14, 165, 233, 0.2)),
      url('~@/assets/images/banner4.svg') center/cover no-repeat;
  }
}

.carousel-btn {
  position: absolute;
  top: 50%;
  z-index: 3;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.35);
  color: #fff;
  font-size: 36px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transform: translateY(-50%);
  transition: background 0.25s, border-color 0.25s;

  span {
    display: block;
    transform: translateY(-2px);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.54);
  }

  &.prev {
    left: 28px;
  }

  &.next {
    right: 28px;
  }
}

.carousel-dots {
  position: absolute;
  left: 50%;
  bottom: 28px;
  z-index: 3;
  display: flex;
  gap: 10px;
  transform: translateX(-50%);

  button {
    width: 34px;
    height: 4px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.34);
    cursor: pointer;
    transition: width 0.25s, background 0.25s;

    &.active {
      width: 54px;
      background: #fff;
    }
  }
}

.section-header {
  margin-bottom: 60px;

  &.center {
    text-align: center;
  }

  .section-tag {
    display: inline-block;
    font-size: 13px;
    color: #3b82f6;
    font-weight: 600;
    letter-spacing: 2px;
    margin-bottom: 12px;
  }

  h2 {
    font-size: 42px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 16px;
  }

  p {
    font-size: 18px;
    color: #64748b;
  }
}

/* Hero */
.hero {
  background:
    linear-gradient(90deg, rgba(226, 232, 240, 0.72) 1px, transparent 1px),
    linear-gradient(0deg, rgba(226, 232, 240, 0.72) 1px, transparent 1px),
    #fff;
  background-size: 96px 96px;
  padding: 88px 30px 76px;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid #e2e8f0;
}

.hero-content {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(0, 1fr);
  gap: 72px;
  align-items: end;

  h1 {
    font-size: 52px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
    margin-bottom: 0;
    letter-spacing: 0;

  }

  > p {
    font-size: 18px;
    color: #475569;
    line-height: 1.9;
    margin-bottom: 30px;
    transform: translateY(56px);
  }
}

.hero-btns {
  display: flex;
  gap: 16px;
}

.btn-primary {
  display: inline-block;
  padding: 14px 36px;
  background: #0f5fa8;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s;
  box-shadow: 0 12px 26px rgba(15, 95, 168, 0.2);

  &:hover {
    transform: translateY(-2px);
    background: #0b4f8a;
    box-shadow: 0 16px 32px rgba(15, 95, 168, 0.26);
  }

  &.large {
    padding: 16px 48px;
    font-size: 18px;
  }
}

.btn-outline {
  display: inline-block;
  padding: 14px 36px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s;

  &:hover {
    border-color: #0f5fa8;
    color: #0f5fa8;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
  }
}

.hero-stats-wrap {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.hero-stats {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
}

.hero-stats + .hero-stats {
  margin-top: 28px;
}

.hero-stats-primary {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.stat-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 28px 30px;
  text-align: left;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.06);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 30px;
    top: 0;
    width: 42px;
    height: 3px;
    background: #0f5fa8;
  }

  .stat-value {
    display: flex;
    align-items: baseline;
    gap: 6px;
    line-height: 1;
  }

  .stat-num {
    font-size: 40px;
    font-weight: 800;
    color: #0f172a;
  }

  .stat-unit {
    font-size: 16px;
    color: #0f5fa8;
    font-weight: 600;
  }

  .stat-label {
    font-size: 15px;
    color: #64748b;
    margin-top: 10px;
  }
}

/* About */
.about-v2 {
  background: #f8fafc;

  .container-v2 {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 480px;
    column-gap: 80px;
    align-items: center;
  }

  .section-header {
    margin-bottom: 16px;

    h2 {
      margin-bottom: 0;
    }
  }
}

.about-left {
  min-width: 0;
}

.about-text {
  p {
    font-size: 17px;
    line-height: 2;
    color: #475569;
    margin-bottom: 20px;
  }

  .link-arrow {
    display: inline-block;
    margin-top: 20px;
    font-size: 16px;
    font-weight: 600;
    color: #3b82f6;

    &:hover {
      color: #06b6d4;
    }
  }
}

.about-img {
  width: 480px;
  flex-shrink: 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.1);

  img {
    width: 100%;
    aspect-ratio: 16 / 9;
    display: block;
    object-fit: cover;
  }

  video {
    width: 100%;
    aspect-ratio: 16 / 9;
    display: block;
    background: #0f172a;
    object-fit: cover;
  }
}

/* Services */
.services-v2 {
  background: #fff;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.service-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 40px 30px;
  transition: all 0.4s;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #06b6d4);
    transform: scaleX(0);
    transition: transform 0.4s;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(0,0,0,0.08);
    border-color: transparent;

    &::before {
      transform: scaleX(1);
    }
  }

  .card-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, #3b82f6, #06b6d4);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 28px;
    margin-bottom: 24px;

    img {
      width: 34px;
      height: 34px;
      object-fit: contain;
    }

    &.has-image {
      background: transparent;
      box-shadow: none;

      img {
        width: 100%;
        height: 100%;
        border-radius: 16px;
      }
    }
  }

  h3 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 12px;
  }

  p {
    font-size: 15px;
    color: #64748b;
    line-height: 1.8;
    margin-bottom: 20px;
  }

  a {
    font-size: 15px;
    font-weight: 600;
    color: #3b82f6;

    &:hover {
      color: #06b6d4;
    }
  }
}

/* Cases */
.cases-v2 {
  background: #f8fafc;
}

.cases-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.case-card-v2 {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
  transition: all 0.4s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.1);
  }

  .case-img-v2 {
    height: 200px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;
    }

    &:hover img {
      transform: scale(1.05);
    }
  }

  .case-info-v2 {
    padding: 24px;

    h4 {
      font-size: 18px;
      font-weight: 600;
      color: #0f172a;
      margin-bottom: 6px;
    }

    p {
      font-size: 14px;
      color: #64748b;
      margin-bottom: 16px;
    }

    a {
      font-size: 14px;
      font-weight: 600;
      color: #3b82f6;

      &:hover {
        color: #06b6d4;
      }
    }
  }
}

/* News */
.news-v2 {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 12% 18%, rgba(59, 130, 246, 0.14), transparent 30%),
    radial-gradient(circle at 86% 24%, rgba(6, 182, 212, 0.14), transparent 28%),
    linear-gradient(135deg, #eef6ff 0%, #f8fbff 48%, #e8f7fb 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(59, 130, 246, 0.055) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59, 130, 246, 0.055) 1px, transparent 1px);
    background-size: 72px 72px;
    -webkit-mask-image: linear-gradient(180deg, transparent, #000 18%, #000 82%, transparent);
    mask-image: linear-gradient(180deg, transparent, #000 18%, #000 82%, transparent);
    pointer-events: none;
  }

  .container-v2 {
    position: relative;
    z-index: 1;
  }
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.news-card-v2 {
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 20px;
  padding: 32px;
  transition: all 0.4s;
  position: relative;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(14px);

  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-6px);
    border-color: rgba(59, 130, 246, 0.18);
    box-shadow: 0 22px 55px rgba(37, 99, 235, 0.14);
  }

  .news-date-v2 {
    margin-bottom: 20px;

    .day {
      display: block;
      font-size: 42px;
      font-weight: 800;
      color: #3b82f6;
      line-height: 1;
    }

    .month {
      font-size: 14px;
      color: #94a3b8;
    }
  }

  h4 {
    font-size: 17px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.6;
    margin-bottom: 20px;
    min-height: 54px;
  }

  a {
    font-size: 14px;
    font-weight: 600;
    color: #3b82f6;

    &:hover {
      color: #06b6d4;
    }
  }
}

/* CTA */
.cta-v2 {
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  padding: 100px 30px;
  text-align: center;

  h2 {
    font-size: 42px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 16px;
  }

  p {
    font-size: 18px;
    color: rgba(255,255,255,0.8);
    margin-bottom: 40px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .btn-primary {
    background: #fff;
    color: #3b82f6;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2);

    &:hover {
      background: #f8fafc;
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
    }
  }
}

@media (max-width: 1200px) {
  .service-grid, .cases-grid, .news-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 960px) {
  .home-carousel {
    height: clamp(410px, 72vw, 680px);
  }

  .carousel-btn {
    display: none;
  }

  .carousel-dots {
    bottom: 22px;
  }

  .hero {
    padding: 64px 24px 56px;
  }

  .hero-content {
    grid-template-columns: 1fr;
    gap: 28px;

    h1 {
      font-size: 36px;
    }

    > p {
      font-size: 16px;
      margin-bottom: 24px;
    }
  }

  .hero-btns {
    flex-wrap: wrap;
  }

  .hero-stats {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 14px;
    margin-top: 0;
  }

  .hero-stats + .hero-stats {
    margin-top: 18px;
  }

  .stat-card {
    padding: 24px 20px;

    &::before {
      left: 20px;
    }

    .stat-num {
      font-size: 32px;
    }
  }

  .about-v2 .container-v2 {
    display: block;
  }

  .about-img {
    width: 100%;
    margin-top: 32px;
  }

  .service-grid, .cases-grid, .news-grid {
    grid-template-columns: 1fr;
  }
}
</style>
